import FormattedDatePicker from "./FormattedDatePicker";
import Visible from "./Visible";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Stack,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import * as dates from "common/dates";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import * as labels from "config.labels";
import { AUTO_STATUS_ON_DOCUMENT_ISSUE } from "flags";
import { useDispatch } from "react-redux";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as featureFlagHooks from "store/hooks/featureFlagHooks";
import * as staticDataHooks from "store/hooks/staticDataHooks";

// When the ui/autoStatusOnDocumentIssue feature flag is on for a company,
// ticking a Document Issued checkbox also bumps layer.status. This keeps the
// document-issued state and the manual status dropdown in sync.
// Untoggling the checkbox does NOT revert the status. Policy Drafted and
// Policy Issued never auto-change the status because the layer is already
// past BOUND at that point.
const DOCUMENT_KEY_TO_STATUS = {
  quote: "QUOTED",
  binder: "BOUND",
};

const Document = ({
  documentKey,
  label,
  layer,
  updateLayer,
  defaultVisible = true,
  legacyKey = null,
  children,
}) => {
  const document = layer?.[documentKey] ?? {};
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { isOn: isAutoStatusOn } = featureFlagHooks.useFeatureFlag(
    AUTO_STATUS_ON_DOCUMENT_ISSUE,
    {
      default: false,
    }
  );
  return (
    <Visible
      byTag={`<DocumentsIssued>.${documentKey}`}
      defaultVisible={defaultVisible}
    >
      <Stack
        direction={"column"}
        spacing={1}
        justifyContent={"flex-start"}
        alignItems={"flex-start"}
      >
        <Disabled ifReadOnly>
          <FormControlLabel
            label={label}
            control={
              <Checkbox
                checked={document.isIssued ?? false}
                onChange={(e) => {
                  const isIssued = e.target.checked;
                  const now = dates.getDate();
                  const autoStatus =
                    isAutoStatusOn && isIssued
                      ? DOCUMENT_KEY_TO_STATUS[documentKey]
                      : null;
                  updateLayer({
                    [documentKey]: {
                      issueDate: isIssued ? dates.toISODateString(now) : null,
                      recordedTimestamp: isIssued ? now.toISOString() : null,
                    },
                    ...(legacyKey != null ? { [legacyKey]: null } : {}),
                    ...(autoStatus != null ? { status: autoStatus } : {}),
                  });
                }}
              />
            }
            componentsProps={{
              typography: { variant: "caption" },
            }}
            sx={{ height: "100%" }}
          />
        </Disabled>
        {(document.isIssued || !!document.issueDate) && (
          <>
            <Disabled ifReadOnly>
              <FormattedDatePicker
                label={translateLabel(labels.DOCUMENT_ISSUANCE_DATE, {
                  default: "Issue Date",
                })}
                variant={"filled"}
                onChangeDate={(issueDate) => {
                  updateLayer({
                    [documentKey]: {
                      ...document,
                      issueDate,
                    },
                  });
                }}
                value={document.issueDate ?? ""}
                fullWidth
                clearable={true}
                sx={{ marginLeft: 5 }}
              />
            </Disabled>
            {children}
          </>
        )}
      </Stack>
    </Visible>
  );
};

const FollowUpEmails = ({ layer, updateLayer }) => {
  const followUpEmails = layer?.followUpEmails ?? { enabled: false, dates: [] };

  const handleToggle = (e) => {
    const enabled = e.target.checked;
    const now = dates.getDate();
    updateLayer({
      followUpEmails: {
        enabled,
        dates: enabled ? [dates.toISODateString(now)] : [],
      },
    });
  };

  const handleAddDate = () => {
    const now = dates.getDate();
    updateLayer({
      followUpEmails: {
        ...followUpEmails,
        dates: [...(followUpEmails.dates ?? []), dates.toISODateString(now)],
      },
    });
  };

  const handleRemoveDate = (index) => {
    const newDates = [...(followUpEmails.dates ?? [])];
    newDates.splice(index, 1);
    updateLayer({
      followUpEmails: {
        ...followUpEmails,
        dates: newDates,
        enabled: newDates.length > 0,
      },
    });
  };

  const handleDateChange = (index, newDate) => {
    const newDates = [...(followUpEmails.dates ?? [])];
    newDates[index] = newDate;
    updateLayer({
      followUpEmails: {
        ...followUpEmails,
        dates: newDates,
      },
    });
  };

  return (
    <Visible byTag={"<DocumentsIssued>.followUpEmails"} defaultVisible={false}>
      <Stack
        direction={"column"}
        spacing={1}
        sx={{ width: "100%", marginTop: 2 }}
      >
        <Disabled ifReadOnly>
          <FormControlLabel
            label={"Follow Up Email Sent?"}
            control={
              <Checkbox
                checked={followUpEmails.enabled ?? false}
                onChange={handleToggle}
              />
            }
            componentsProps={{
              typography: { variant: "caption" },
            }}
          />
        </Disabled>
        {followUpEmails.enabled && (
          <Stack direction={"column"} spacing={1} sx={{ marginLeft: 5 }}>
            {(followUpEmails.dates ?? []).map((date, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Disabled ifReadOnly>
                  <FormattedDatePicker
                    label={`Follow Up Date ${index + 1}`}
                    variant={"filled"}
                    onChangeDate={(newDate) => handleDateChange(index, newDate)}
                    value={date ?? ""}
                    fullWidth
                    clearable={true}
                  />
                </Disabled>
                <Disabled ifReadOnly>
                  <IconButton
                    size={"small"}
                    onClick={() => handleRemoveDate(index)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon fontSize={"small"} />
                  </IconButton>
                </Disabled>
              </Box>
            ))}
            <Disabled ifReadOnly>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  size={"small"}
                  onClick={handleAddDate}
                  color={"primary"}
                >
                  <AddIcon fontSize={"small"} />
                </IconButton>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {"Add another follow up date"}
                </Typography>
              </Box>
            </Disabled>
          </Stack>
        )}
      </Stack>
    </Visible>
  );
};

const PolicyIssuanceStatusFields = ({ layer, updateLayer, translateLabel }) => {
  return (
    <Visible
      byTag={"<DocumentsIssued>.policyIssuanceStatus"}
      defaultVisible={false}
    >
      <Stack
        direction={"column"}
        spacing={1}
        sx={{ width: "100%", marginTop: 1, marginLeft: 5 }}
      >
        <Visible
          byTag={"<DocumentsIssued>.policyIssuanceStatus.dropdown"}
          defaultVisible={true}
        >
          <Disabled ifReadOnly>
            <FormControl variant={"filled"} fullWidth size={"small"}>
              <InputLabel shrink>
                {translateLabel(labels.POLICY_ISSUANCE_STATUS, {
                  default: "Policy Issuance Status",
                })}
              </InputLabel>
              <Select
                value={layer?.policyIssuanceStatus?.status ?? "__NONE__"}
                onChange={(e) =>
                  updateLayer({
                    policyIssuanceStatus: {
                      ...layer?.policyIssuanceStatus,
                      status:
                        e.target.value === "__NONE__" ? null : e.target.value,
                    },
                  })
                }
                label={translateLabel(labels.POLICY_ISSUANCE_STATUS, {
                  default: "Policy Issuance Status",
                })}
              >
                <MenuItem value={"__NONE__"}>{"None"}</MenuItem>
                <MenuItem value={"EXTERNAL"}>{"External"}</MenuItem>
                <MenuItem value={"INTERNAL"}>{"Internal"}</MenuItem>
              </Select>
            </FormControl>
          </Disabled>
        </Visible>
        <Disabled ifReadOnly>
          <TextField
            label={"Comments"}
            value={layer?.policyIssuanceStatus?.comments ?? ""}
            onChange={(e) =>
              updateLayer({
                policyIssuanceStatus: {
                  ...layer?.policyIssuanceStatus,
                  comments: e.target.value,
                },
              })
            }
            variant={"filled"}
            fullWidth
            multiline
            minRows={2}
            InputLabelProps={{ shrink: true }}
          />
        </Disabled>
      </Stack>
    </Visible>
  );
};

const DocumentsIssued = ({ layer }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const dispatch = useDispatch();

  const updateLayer = (values) => {
    if (!layer?.id) {
      return;
    }
    dispatch(
      pricingActions.updateLayer({
        layerId: layer.id,
        values,
      })
    );
  };

  return (
    <Component
      title={translateLabel(labels.DOCUMENTS_ISSUED, {
        default: "Documents Issued",
      })}
      growVertically
    >
      <Stack direction={"column"} spacing={1}>
        <Document
          documentKey={"quote"}
          label={translateLabel(labels.IS_QUOTE_ISSUED, {
            default: "Quote Issued?",
          })}
          layer={layer}
          updateLayer={updateLayer}
        />
        <Document
          documentKey={"binder"}
          label={translateLabel(labels.IS_BINDER_ISSUED, {
            default: "Binder Issued?",
          })}
          layer={layer}
          updateLayer={updateLayer}
          legacyKey={"isBinderIssued"}
        />
        <Document
          documentKey={"policyDrafted"}
          label={translateLabel(labels.IS_POLICY_DRAFTED, {
            default: "Policy Drafted?",
          })}
          layer={layer}
          updateLayer={updateLayer}
          defaultVisible={false}
        />
        <Document
          documentKey={"policy"}
          label={translateLabel(labels.IS_POLICY_ISSUED, {
            default: "Policy Issued?",
          })}
          layer={layer}
          updateLayer={updateLayer}
          legacyKey={"isPolicyIssued"}
        />
        <PolicyIssuanceStatusFields
          layer={layer}
          updateLayer={updateLayer}
          translateLabel={translateLabel}
        />
        <FollowUpEmails layer={layer} updateLayer={updateLayer} />
        <Document
          documentKey={"puniQuote"}
          label={translateLabel(labels.IS_PUNI_QUOTE_ISSUED, {
            default: "Puni Quote Issued?",
          })}
          layer={layer}
          updateLayer={updateLayer}
          defaultVisible={false}
        />
        <Document
          documentKey={"puniBinder"}
          label={translateLabel(labels.IS_PUNI_BINDER_ISSUED, {
            default: "Puni Binder Issued?",
          })}
          layer={layer}
          updateLayer={updateLayer}
          defaultVisible={false}
        />
        <Document
          documentKey={"puniPolicy"}
          label={translateLabel(labels.IS_PUNI_POLICY_ISSUED, {
            default: "Puni Policy Issued?",
          })}
          layer={layer}
          updateLayer={updateLayer}
          defaultVisible={false}
        />
      </Stack>
    </Component>
  );
};

export default DocumentsIssued;
